import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ApiConstants } from '../../../constants/api.constants';
import { Employee, EmployeeRoot } from '../../../domain/employee-domain';
import { TimesheetLine, TimesheetLineRoot } from '../../../domain/timesheets-domain';
import { ProjectRoot } from '../../../domain/projects-domain';
import { PhaseRoot } from '../../../domain/phases-domain';

@Component({
  selector: 'app-reporting',
  standalone: true,
  imports: [MatSelectModule, MatButtonModule, MatTableModule, MatProgressSpinnerModule, MatFormFieldModule],
  templateUrl: './reporting.component.html',
})
export class ReportingComponent implements OnInit {
  private _http = inject(HttpClient);

  employees = signal<Employee[]>([]);
  isLoadingEmployees = signal(false);

  private _projectNames = signal<Map<string, string>>(new Map());
  private _phaseNames = signal<Map<string, string>>(new Map());

  selectedEmployeeNo = signal<string | null>(null);
  selectedMonth = signal(new Date().getMonth() + 1);
  selectedYear = signal(new Date().getFullYear());

  isSearching = signal(false);
  lines = signal<TimesheetLine[]>([]);
  hasSearched = signal(false);

  readonly months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  readonly years = (() => {
    const y = new Date().getFullYear();
    return [y - 2, y - 1, y, y + 1];
  })();

  readonly displayedColumns = ['activityDate', 'projectNo', 'phaseNo', 'taskNo', 'totalTime', 'billable', 'comment'];

  get totalHours(): number {
    return this.lines().reduce((sum, l) => sum + l.totalTime, 0);
  }

  getProjectName(projectNo: string): string {
    return this._projectNames().get(projectNo) ?? projectNo;
  }

  getPhaseName(projectNo: string, phaseNo: string): string {
    return this._phaseNames().get(`${projectNo}|${phaseNo}`) ?? phaseNo;
  }

  ngOnInit(): void {
    this.isLoadingEmployees.set(true);
    forkJoin({
      employees: this._http.get<EmployeeRoot>(ApiConstants.EMPLOYEE_LIST_API_URL),
      projects: this._http.get<ProjectRoot>(ApiConstants.PROJECT_LIST_API_URL),
      phases: this._http.get<PhaseRoot>(ApiConstants.PHASE_LIST_API_URL),
    }).subscribe({
      next: ({ employees, projects, phases }) => {
        this.employees.set([...employees.value].sort((a, b) => a.displayName.localeCompare(b.displayName)));
        this._projectNames.set(new Map(projects.value.map(p => [p.projectNo, p.projectName])));
        this._phaseNames.set(new Map(phases.value.map(p => [`${p.projectNo}|${p.phaseNo}`, p.phaseName])));
        this.isLoadingEmployees.set(false);
      },
      error: () => this.isLoadingEmployees.set(false),
    });
  }

  search(): void {
    const employeeNo = this.selectedEmployeeNo();
    if (!employeeNo) return;

    const year = this.selectedYear();
    const month = this.selectedMonth();
    const pad = (n: number) => String(n).padStart(2, '0');
    const start = `${year}-${pad(month)}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const end = `${year}-${pad(month)}-${lastDay}`;

    const filter = `employeeNo eq '${employeeNo}' and activityDate ge ${start} and activityDate le ${end}`;
    const url = `${ApiConstants.TIMESHEET_LINE_LIST_API_URL}?$filter=${encodeURIComponent(filter)}&$orderby=activityDate`;

    this.isSearching.set(true);
    this._http.get<TimesheetLineRoot>(url).subscribe({
      next: (res) => {
        this.lines.set(res.value);
        this.hasSearched.set(true);
        this.isSearching.set(false);
      },
      error: () => {
        this.lines.set([]);
        this.hasSearched.set(true);
        this.isSearching.set(false);
      },
    });
  }
}
